import React, { useState, useEffect, useContext } from 'react';
import API from '../utils/API';
import { AuthContext } from '../utils/AuthContext';

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const periodKey = (dateStr, mode) => {
    const d = new Date(dateStr);
    if (mode === 'month') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (mode === 'quarter') return `${d.getFullYear()} Q${Math.floor(d.getMonth() / 3) + 1}`;
    return 'All-time';
};

export default function AdminAmbassadors({ onActiveChange }) {
    const { auth, refreshStatus } = useContext(AuthContext);
    const [ambassadors, setAmbassadors] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editingId, setEditingId] = useState(null);
    const [draftMonthly, setDraftMonthly] = useState('');
    const [draftAnnual, setDraftAnnual] = useState('');
    const [savingRate, setSavingRate] = useState({});
    const [togglingActive, setTogglingActive] = useState({});

    const [bulkMonthly, setBulkMonthly] = useState('');
    const [bulkAnnual, setBulkAnnual] = useState('');
    const [bulkSaving, setBulkSaving] = useState(false);
    const [bulkStatus, setBulkStatus] = useState('');

    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [periodMode, setPeriodMode] = useState('all');

    const [payoutForm, setPayoutForm] = useState({ period_start: '', period_end: '', amount: '', paid_at: '', note: '' });
    const [payoutSaving, setPayoutSaving] = useState(false);
    const [payoutError, setPayoutError] = useState('');
    const [deletingPayoutId, setDeletingPayoutId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await API.adminGetAmbassadors();
            setAmbassadors(res.data);
        } catch (err) {
            console.error('Failed to load ambassadors:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadDefaultRates = async () => {
        try {
            const res = await API.adminGetAmbassadorSettings();
            setBulkMonthly(String(res.data.commission_rate_monthly));
            setBulkAnnual(String(res.data.commission_rate_annual));
        } catch (err) {
            console.error('Failed to load standing ambassador rate:', err);
        }
    };

    useEffect(() => { load(); loadDefaultRates(); }, []);

    const startEditRate = (row) => {
        setEditingId(row.ambassador_id);
        setDraftMonthly(String(row.commission_rate_monthly));
        setDraftAnnual(String(row.commission_rate_annual));
    };

    const cancelEditRate = () => {
        setEditingId(null);
        setDraftMonthly('');
        setDraftAnnual('');
    };

    const saveRate = async (row) => {
        setSavingRate(s => ({ ...s, [row.ambassador_id]: true }));
        try {
            await API.adminUpdateAmbassadorRate(row.ambassador_id, {
                commission_rate_monthly: draftMonthly,
                commission_rate_annual: draftAnnual,
            });
            cancelEditRate();
            await load();
        } catch (err) {
            console.error('Failed to update rate:', err);
        } finally {
            setSavingRate(s => ({ ...s, [row.ambassador_id]: false }));
        }
    };

    const toggleActive = async (row) => {
        const user_id = row.UserAccount?.user_id;
        if (!user_id) return;
        setTogglingActive(s => ({ ...s, [row.ambassador_id]: true }));
        try {
            if (row.active) await API.adminUnsetUserAmbassador(user_id);
            else await API.adminSetUserAmbassador(user_id);
            setAmbassadors(prev => prev.map(a => a.ambassador_id === row.ambassador_id ? { ...a, active: !row.active } : a));
            if (user_id === auth.user?.user_id) refreshStatus();
            onActiveChange?.();
        } catch (err) {
            console.error('Failed to toggle ambassador active status:', err);
        } finally {
            setTogglingActive(s => ({ ...s, [row.ambassador_id]: false }));
        }
    };

    const handleBulkApply = async (e) => {
        e.preventDefault();
        if (!bulkMonthly && !bulkAnnual) return;
        setBulkSaving(true);
        setBulkStatus('');
        try {
            await API.adminBulkUpdateAmbassadorRate({
                commission_rate_monthly: bulkMonthly,
                commission_rate_annual: bulkAnnual,
            });
            setBulkStatus('Standing rate saved and applied to all active ambassadors.');
            await load();
        } catch (err) {
            setBulkStatus('Failed to apply bulk rate.');
            console.error('Failed to bulk update rate:', err);
        } finally {
            setBulkSaving(false);
        }
    };

    const openDetail = async (ambassador_id) => {
        setLoadingDetail(true);
        setPeriodMode('all');
        setPayoutError('');
        setPayoutForm({ period_start: '', period_end: '', amount: '', paid_at: '', note: '' });
        try {
            const res = await API.adminGetAmbassadorDetail(ambassador_id);
            setDetail(res.data);
        } catch (err) {
            console.error('Failed to load ambassador detail:', err);
        } finally {
            setLoadingDetail(false);
        }
    };

    const closeDetail = () => setDetail(null);

    const refreshDetail = async (ambassador_id) => {
        try {
            const res = await API.adminGetAmbassadorDetail(ambassador_id);
            setDetail(res.data);
        } catch (err) {
            console.error('Failed to refresh ambassador detail:', err);
        }
    };

    const handleCreatePayout = async (e) => {
        e.preventDefault();
        const { period_start, period_end, amount, paid_at } = payoutForm;
        if (!period_start || !period_end || !amount || !paid_at) {
            setPayoutError('Period start, period end, amount, and paid date are required.');
            return;
        }
        setPayoutSaving(true);
        setPayoutError('');
        try {
            await API.adminCreateAmbassadorPayout(detail.ambassador_id, payoutForm);
            setPayoutForm({ period_start: '', period_end: '', amount: '', paid_at: '', note: '' });
            await refreshDetail(detail.ambassador_id);
            await load();
        } catch (err) {
            setPayoutError(err.response?.data?.msg || 'Failed to record payout.');
        } finally {
            setPayoutSaving(false);
        }
    };

    const handleDeletePayout = async (payout_id) => {
        setDeletingPayoutId(payout_id);
        try {
            await API.adminDeleteAmbassadorPayout(detail.ambassador_id, payout_id);
            await refreshDetail(detail.ambassador_id);
            await load();
        } catch (err) {
            console.error('Failed to delete payout:', err);
        } finally {
            setDeletingPayoutId(null);
        }
    };

    // Derived totals + period summary for the open detail modal
    const referrals = detail?.AmbassadorReferrals || [];
    const payouts = detail?.AmbassadorPayouts || [];
    const allCommissions = referrals.flatMap(r => (r.AmbassadorCommissions || []).map(c => ({ ...c, referral: r })));
    const totalCharged = allCommissions.reduce((sum, c) => sum + Number(c.amount_charged || 0), 0);
    const totalOwed = allCommissions.reduce((sum, c) => sum + Number(c.commission_amount || 0), 0);
    const totalPaid = payouts.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const balanceDue = totalOwed - totalPaid;

    const periodSummary = (() => {
        const buckets = {};
        referrals.forEach(r => {
            if (!r.subscribed_at) return;
            const key = periodKey(r.subscribed_at, periodMode);
            buckets[key] = buckets[key] || { key, referrals: 0, commission: 0 };
            buckets[key].referrals += 1;
        });
        allCommissions.forEach(c => {
            if (!c.charged_at) return;
            const key = periodKey(c.charged_at, periodMode);
            buckets[key] = buckets[key] || { key, referrals: 0, commission: 0 };
            buckets[key].commission += Number(c.commission_amount || 0);
        });
        return Object.values(buckets).sort((a, b) => b.key.localeCompare(a.key));
    })();

    return (
        <div>
            <p className="text-muted small mb-3">
                Ambassadors earn commission for subscribers they refer. The rate below is the standing default —
                it's applied automatically to any user newly designated as an ambassador, and saving it here also
                applies it to every currently active ambassador. Individual rates can still be edited per-row below.
                Click an ambassador to see who they've referred and record payouts.
            </p>

            <form className="ai-add-row" onSubmit={handleBulkApply}>
                <div className="ai-field">
                    <label className="ai-field-label" htmlFor="bulk-monthly-rate">Monthly Rate</label>
                    <input
                        id="bulk-monthly-rate"
                        className="admin-search-input"
                        style={{ maxWidth: 160 }}
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Monthly rate %"
                        title="Monthly commission rate (%)"
                        value={bulkMonthly}
                        onChange={e => setBulkMonthly(e.target.value)}
                    />
                </div>
                <div className="ai-field">
                    <label className="ai-field-label" htmlFor="bulk-annual-rate">Annual Rate</label>
                    <input
                        id="bulk-annual-rate"
                        className="admin-search-input"
                        style={{ maxWidth: 160 }}
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Annual rate %"
                        title="Annual commission rate (%)"
                        value={bulkAnnual}
                        onChange={e => setBulkAnnual(e.target.value)}
                    />
                </div>
                <button className="awards-save-btn" type="submit" disabled={bulkSaving || (!bulkMonthly && !bulkAnnual)}>
                    {bulkSaving ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Save Standing Rate'}
                </button>
            </form>
            {bulkStatus && <div className="ai-error mb-2">{bulkStatus}</div>}

            {loading ? (
                <div className="admin-loading">Loading…</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th className="admin-th-center">Monthly Rate</th>
                                <th className="admin-th-center">Annual Rate</th>
                                <th className="admin-th-center">Referrals</th>
                                <th className="admin-th-center">Owed</th>
                                <th className="admin-th-center">Paid</th>
                                <th className="admin-th-center">Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ambassadors.length === 0 ? (
                                <tr><td colSpan={8} className="admin-empty">No ambassadors yet — toggle a user on from Registered Users.</td></tr>
                            ) : ambassadors.map(row => (
                                <tr key={row.ambassador_id}>
                                    <td className="admin-name" data-label="Name">
                                        <button className="admin-name-btn" onClick={() => openDetail(row.ambassador_id)}>
                                            {row.UserAccount?.first_name} {row.UserAccount?.last_name}
                                        </button>
                                    </td>
                                    <td data-label="Email">{row.UserAccount?.email}</td>
                                    <td className="admin-td-center" data-label="Monthly Rate">
                                        {editingId === row.ambassador_id ? (
                                            <input
                                                className="admin-email-input"
                                                style={{ maxWidth: 80 }}
                                                type="number"
                                                step="0.01"
                                                value={draftMonthly}
                                                onChange={e => setDraftMonthly(e.target.value)}
                                            />
                                        ) : (
                                            <span className="admin-email-text" onClick={() => startEditRate(row)} title="Click to edit">
                                                {fmtPct(row.commission_rate_monthly)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="admin-td-center" data-label="Annual Rate">
                                        {editingId === row.ambassador_id ? (
                                            <input
                                                className="admin-email-input"
                                                style={{ maxWidth: 80 }}
                                                type="number"
                                                step="0.01"
                                                value={draftAnnual}
                                                onChange={e => setDraftAnnual(e.target.value)}
                                            />
                                        ) : (
                                            <span className="admin-email-text" onClick={() => startEditRate(row)} title="Click to edit">
                                                {fmtPct(row.commission_rate_annual)}
                                            </span>
                                        )}
                                        {editingId === row.ambassador_id && (
                                            <div className="admin-email-actions d-inline-flex ms-1">
                                                <button
                                                    className="admin-email-save"
                                                    onClick={() => saveRate(row)}
                                                    disabled={savingRate[row.ambassador_id]}
                                                    title="Save"
                                                >
                                                    {savingRate[row.ambassador_id]
                                                        ? <i className="bi bi-arrow-repeat admin-spin" />
                                                        : <i className="bi bi-check-lg" />}
                                                </button>
                                                <button className="admin-email-cancel" onClick={cancelEditRate} title="Cancel">
                                                    <i className="bi bi-x-lg" />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="admin-td-center" data-label="Referrals">{row.referral_count ?? 0}</td>
                                    <td className="admin-td-center" data-label="Owed">{fmtMoney(row.total_owed)}</td>
                                    <td className="admin-td-center" data-label="Paid">{fmtMoney(row.total_paid)}</td>
                                    <td className="admin-td-center" data-label="Active">
                                        <button
                                            className={`admin-toggle ${row.active ? 'on' : 'off'}`}
                                            onClick={() => toggleActive(row)}
                                            disabled={togglingActive[row.ambassador_id]}
                                            title={row.active ? 'Deactivate ambassador' : 'Reactivate ambassador'}
                                        >
                                            {togglingActive[row.ambassador_id]
                                                ? <i className="bi bi-arrow-repeat admin-spin" />
                                                : row.active
                                                ? <i className="bi bi-toggle-on" />
                                                : <i className="bi bi-toggle-off" />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(detail || loadingDetail) && (
                <div className="admin-modal-overlay" onClick={closeDetail}>
                    <div className="admin-modal admin-modal--wide" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <span className="admin-modal-title">
                                {detail ? `${detail.UserAccount?.first_name} ${detail.UserAccount?.last_name}` : 'Loading…'}
                            </span>
                            <button className="admin-modal-close" onClick={closeDetail}>
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>

                        {loadingDetail && !detail ? (
                            <div className="admin-modal-body admin-modal-loading">
                                <i className="bi bi-arrow-repeat admin-spin" /> Loading…
                            </div>
                        ) : detail && (
                            <div className="admin-modal-body">

                                <div className="admin-modal-section">
                                    <div className="admin-modal-section-title">Balance</div>
                                    <div className="admin-modal-row"><span>Total Charged</span><span>{fmtMoney(totalCharged)}</span></div>
                                    <div className="admin-modal-row"><span>Total Commission Owed</span><span>{fmtMoney(totalOwed)}</span></div>
                                    <div className="admin-modal-row"><span>Total Paid</span><span>{fmtMoney(totalPaid)}</span></div>
                                    <div className="admin-modal-row"><span>Balance Due</span><span>{fmtMoney(balanceDue)}</span></div>
                                </div>

                                <div className="admin-modal-section">
                                    <div className="admin-modal-section-title">Referred Subscribers ({referrals.length})</div>
                                    <div className="awards-subtabs mb-2">
                                        <button className={`awards-subtab ${periodMode === 'all' ? 'active' : ''}`} onClick={() => setPeriodMode('all')}>All-time</button>
                                        <button className={`awards-subtab ${periodMode === 'month' ? 'active' : ''}`} onClick={() => setPeriodMode('month')}>Monthly</button>
                                        <button className={`awards-subtab ${periodMode === 'quarter' ? 'active' : ''}`} onClick={() => setPeriodMode('quarter')}>Quarterly</button>
                                    </div>
                                    {periodMode !== 'all' && (
                                        <div className="admin-table-wrapper mb-3">
                                            <table className="admin-table">
                                                <thead>
                                                    <tr><th>Period</th><th className="admin-th-center">New Referrals</th><th className="admin-th-center">Commission Earned</th></tr>
                                                </thead>
                                                <tbody>
                                                    {periodSummary.length === 0 ? (
                                                        <tr><td colSpan={3} className="admin-empty">No activity yet</td></tr>
                                                    ) : periodSummary.map(p => (
                                                        <tr key={p.key}>
                                                            <td>{p.key}</td>
                                                            <td className="admin-td-center">{p.referrals}</td>
                                                            <td className="admin-td-center">{fmtMoney(p.commission)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {referrals.length === 0 ? (
                                        <p className="admin-team-org-none">No referrals yet.</p>
                                    ) : (
                                        <div className="admin-table-wrapper">
                                            <table className="admin-table">
                                                <thead>
                                                    <tr>
                                                        <th>Subscriber</th>
                                                        <th>Plan</th>
                                                        <th>Subscribed</th>
                                                        <th>Status</th>
                                                        <th>Charges</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {referrals.map(r => (
                                                        <tr key={r.referral_id}>
                                                            <td data-label="Subscriber">
                                                                <div>{r.UserAccount?.first_name} {r.UserAccount?.last_name}</div>
                                                                <div className="admin-trial-badge">{r.UserAccount?.email}</div>
                                                                {(r.UserAccount?.ProfileInfo?.mobile_phone || r.UserAccount?.ProfileInfo?.other_phone) && (
                                                                    <div className="admin-trial-badge">{r.UserAccount.ProfileInfo.mobile_phone || r.UserAccount.ProfileInfo.other_phone}</div>
                                                                )}
                                                            </td>
                                                            <td data-label="Plan">{r.plan_type} / {r.billing_interval}</td>
                                                            <td data-label="Subscribed">{fmtDate(r.subscribed_at)}</td>
                                                            <td data-label="Status">{r.status}</td>
                                                            <td data-label="Charges">
                                                                {(!r.AmbassadorCommissions || r.AmbassadorCommissions.length === 0) ? '—' : (
                                                                    <ul className="ps-3 mb-0" style={{ fontSize: '0.78rem' }}>
                                                                        {r.AmbassadorCommissions.map(c => (
                                                                            <li key={c.commission_id}>
                                                                                {fmtDate(c.charged_at)}: {fmtMoney(c.amount_charged)} charged &rarr; {fmtMoney(c.commission_amount)} commission
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                <div className="admin-modal-section">
                                    <div className="admin-modal-section-title">Record Payout</div>
                                    <form onSubmit={handleCreatePayout}>
                                        <div className="admin-trial-row mb-2">
                                            <input
                                                className="admin-search-input"
                                                type="date"
                                                value={payoutForm.period_start}
                                                onChange={e => setPayoutForm(f => ({ ...f, period_start: e.target.value }))}
                                            />
                                            <input
                                                className="admin-search-input"
                                                type="date"
                                                value={payoutForm.period_end}
                                                onChange={e => setPayoutForm(f => ({ ...f, period_end: e.target.value }))}
                                            />
                                        </div>
                                        <div className="admin-trial-row mb-2">
                                            <input
                                                className="admin-search-input"
                                                type="number"
                                                step="0.01"
                                                placeholder="Amount paid"
                                                value={payoutForm.amount}
                                                onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                                            />
                                            <input
                                                className="admin-search-input"
                                                type="date"
                                                value={payoutForm.paid_at}
                                                onChange={e => setPayoutForm(f => ({ ...f, paid_at: e.target.value }))}
                                            />
                                        </div>
                                        <div className="admin-trial-row mb-2">
                                            <input
                                                className="admin-search-input"
                                                type="text"
                                                placeholder="Note (optional)"
                                                value={payoutForm.note}
                                                onChange={e => setPayoutForm(f => ({ ...f, note: e.target.value }))}
                                            />
                                        </div>
                                        {payoutError && <div className="ai-error mb-2">{payoutError}</div>}
                                        <button className="awards-save-btn" type="submit" disabled={payoutSaving}>
                                            {payoutSaving ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Record Payout'}
                                        </button>
                                    </form>

                                    {payouts.length > 0 && (
                                        <div className="admin-table-wrapper mt-3">
                                            <table className="admin-table">
                                                <thead>
                                                    <tr>
                                                        <th>Period</th>
                                                        <th className="admin-th-center">Amount</th>
                                                        <th className="admin-th-center">Paid</th>
                                                        <th>Note</th>
                                                        <th className="admin-th-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {payouts.map(p => (
                                                        <tr key={p.payout_id}>
                                                            <td>{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</td>
                                                            <td className="admin-td-center">{fmtMoney(p.amount)}</td>
                                                            <td className="admin-td-center">{fmtDate(p.paid_at)}</td>
                                                            <td>{p.note || '—'}</td>
                                                            <td className="admin-td-center">
                                                                <button
                                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                                    onClick={() => handleDeletePayout(p.payout_id)}
                                                                    disabled={deletingPayoutId === p.payout_id}
                                                                >
                                                                    {deletingPayoutId === p.payout_id
                                                                        ? <i className="bi bi-arrow-repeat admin-spin" />
                                                                        : <i className="bi bi-trash" />}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
